/**
 * Application Configuration
 * Centralized settings for the entire application
 * This file contains global configuration values that affect the behavior and appearance of the app
 * 
 * Note: Some values can be overridden by ApplicationSettings from the database.
 * Use the SettingsContext to access merged configuration values.
 */

export const APP_CONFIG = {
  // ============================================================================
  // DATE & TIME FORMATTING
  // ============================================================================
  
  /**
   * Date format used throughout the application
   * Format: DD/MM/YYYY
   */
  dateFormat: 'DD/MM/YYYY',
  
  /**
   * Date-time format for detailed timestamps
   * Format: DD/MM/YYYY HH:mm
   */
  dateTimeFormat: 'DD/MM/YYYY HH:mm',
  
  /**
   * Time format for time-only displays
   * Format: HH:mm
   */
  timeFormat: 'HH:mm',
  
  /**
   * Date format for API requests/responses (ISO format)
   * Format: YYYY-MM-DD
   */
  apiDateFormat: 'YYYY-MM-DD',
  
  /**
   * DateTime format for API requests/responses
   * Format: ISO 8601
   */
  apiDateTimeFormat: 'YYYY-MM-DDTHH:mm:ss.SSSZ',

  // ============================================================================
  // LOCALE SETTINGS
  // ============================================================================
  
  /**
   * Locale for date formatting
   * Using 'en-GB' for DD/MM/YYYY format
   */
  dateLocale: 'en-GB',
  
  /**
   * Locale options for date formatting
   */
  dateLocaleOptions: {
    day: '2-digit' as const,
    month: '2-digit' as const,
    year: 'numeric' as const,
  },

  // ============================================================================
  // TABLE CONFIGURATION
  // ============================================================================
  
  table: {
    /**
     * Default page size for tables
     */
    defaultPageSize: 200,
    
    /**
     * Available page size options
     */
    pageSizeOptions: [50, 100, 200, 500],
    
    /**
     * Default table density
     */
    defaultDensity: 'normal' as const, // 'compact', 'normal', 'comfortable'
    
    /**
     * Table density configurations
     */
    densityConfig: {
      compact: {
        description: 'Tight spacing, more rows visible',
        rowHeight: 'h-8',
        cellPadding: 'px-3 py-1.5',
        headerPadding: 'px-3 py-2',
      },
      normal: {
        description: 'Standard spacing, balanced view',
        rowHeight: 'h-10',
        cellPadding: 'px-4 py-2',
        headerPadding: 'px-4 py-3',
      },
      comfortable: {
        description: 'Loose spacing, easier to read',
        rowHeight: 'h-12',
        cellPadding: 'px-6 py-3',
        headerPadding: 'px-6 py-4',
      },
    },
    
    /**
     * Available density options
     */
    densityOptions: ['compact', 'normal', 'comfortable'] as const,
    
    /**
     * Default date column width
     */
    dateColumnWidth: '120px',
    
    /**
     * Default datetime column width
     */
    dateTimeColumnWidth: '150px',
  },

  // ============================================================================
  // API SETTINGS
  // ============================================================================
  
  api: {
    /**
     * Request timeout (in milliseconds)
     * Can be overridden by ApplicationSettings.apiTimeout
     *
     * Lowered from 30000 to 10000 to avoid the UI
     * feeling frozen for a long time when the backend
     * is unreachable or very slow.
     */
    timeout: 10000,
    
    /**
     * Retry attempts for failed requests
     * Can be overridden by ApplicationSettings.apiRetryAttempts
     */
    retryAttempts: 3,
    
    /**
     * Retry delay (in milliseconds)
     * Can be overridden by ApplicationSettings.apiRetryDelay
     */
    retryDelay: 1000,
    
    /**
     * SWR cache duration (in milliseconds)
     * Can be overridden by ApplicationSettings.apiCacheDuration
     */
    cacheDuration: 300000, // 5 minutes
  },

  // ============================================================================
  // FEATURE FLAGS
  // ============================================================================
  
  features: {
    /**
     * Enable advanced search functionality
     * Can be overridden by ApplicationSettings.enableAdvancedSearch
     */
    enableAdvancedSearch: true,
    
    /**
     * Enable export functionality
     * Can be overridden by ApplicationSettings.enableExport
     */
    enableExport: true,
    
    /**
     * Enable bulk operations
     * Can be overridden by ApplicationSettings.enableBulkOperations
     */
    enableBulkOperations: true,
    
    /**
     * Enable theme mode
     * Can be overridden by ApplicationSettings.enableDarkMode
     */
    enableDarkMode: true,
    
    /**
     * Enable table presets
     * Can be overridden by ApplicationSettings.enableTablePresets
     */
    enableTablePresets: true,
    
    /**
     * Enable conditional formatting
     * Can be overridden by ApplicationSettings.enableConditionalFormatting
     */
    enableConditionalFormatting: true,
    
    /**
     * Show application footer
     * Can be overridden by ApplicationSettings.enableFooter
     */
    enableFooter: true,
    
    /**
     * Enable real-time updates
     * Can be overridden by ApplicationSettings.enableRealTimeUpdates
     */
    enableRealTimeUpdates: false,
    
    /**
     * Enable audit logging
     * Can be overridden by ApplicationSettings.enableAuditLogging
     */
    enableAuditLogging: true,
  },

  // ============================================================================
  // DASHBOARD GRID (default widget layout)
  // ============================================================================
  /** Per row = gridColumns ÷ width. Default: 6 stat, 3 list, 2 chart, 1 full per row */
  dashboard: {
    gridColumns: 6,
    statCardWidth: 1,
    listWidgetWidth: 2,
    chartWidth: 3,
    fullWidth: 6,
  },

  // ============================================================================
  // SYSTEM SETTINGS
  // ============================================================================
  
  system: {
    /**
     * Application name
     * Can be overridden by ApplicationSettings.appName
     */
    appName: 'UMS Application',
    
    /**
     * Application version
     */
    version: '1.0.0',
    
    /**
     * Environment
     */
    environment: process.env.NODE_ENV || 'development',
    
    /**
     * Debug mode
     */
    debugMode: process.env.NODE_ENV === 'development',
    
    /**
     * Session timeout (in milliseconds)
     * Can be overridden by ApplicationSettings.sessionTimeout
     */
    sessionTimeout: 8 * 60 * 60 * 1000, // 8 hours
  },
};

/**
 * Format a date string or Date object to DD/MM/YYYY format
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted date string in DD/MM/YYYY format, or empty string if invalid
 */
export const formatDate = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    return dateObj.toLocaleDateString(
      APP_CONFIG.dateLocale,
      APP_CONFIG.dateLocaleOptions
    );
  } catch {
    return '';
  }
};

/**
 * Format a date for filter comparison (locale-independent).
 * Uses UTC and fixed DD/MM/YYYY so filtering works identically on Windows and Linux/Docker
 * where toLocaleDateString can differ or lack en-GB support.
 * Use this for date filter getValue and applyFilter, not for general display.
 */
export const formatDateForFilter = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';
    const day = String(dateObj.getUTCDate()).padStart(2, '0');
    const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
    const year = dateObj.getUTCFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return '';
  }
};

/**
 * Format a date string or Date object to DD/MM/YYYY HH:mm format
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted date-time string in DD/MM/YYYY HH:mm format, or empty string if invalid
 */
export const formatDateTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    const dateStr = dateObj.toLocaleDateString(
      APP_CONFIG.dateLocale,
      APP_CONFIG.dateLocaleOptions
    );
    
    const timeStr = dateObj.toLocaleTimeString(
      APP_CONFIG.dateLocale,
      {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }
    );
    
    return `${dateStr} ${timeStr}`;
  } catch {
    return '';
  }
};

/**
 * Format a date for the email list: if the date is today, show only time; otherwise show full date-time.
 * @param date - Date string, Date object, or null/undefined
 * @returns Time only (e.g. "14:30") when today, otherwise DD/MM/YYYY HH:mm
 */
export const formatDateTimeForList = (date: string | Date | null | undefined): string => {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    const today = new Date();
    const isToday =
      dateObj.getDate() === today.getDate() &&
      dateObj.getMonth() === today.getMonth() &&
      dateObj.getFullYear() === today.getFullYear();

    if (isToday) {
      return dateObj.toLocaleTimeString(APP_CONFIG.dateLocale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    }

    return formatDateTime(date);
  } catch {
    return '';
  }
};

/**
 * Format a date string or Date object to time only (HH:mm format)
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted time string in HH:mm format, or empty string if invalid
 */
export const formatTime = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    return dateObj.toLocaleTimeString(
      APP_CONFIG.dateLocale,
      {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }
    );
  } catch {
    return '';
  }
};

/**
 * Format a date for API requests (YYYY-MM-DD format)
 * @param date - Date string, Date object, or null/undefined
 * @returns Formatted date string in YYYY-MM-DD format, or empty string if invalid
 */
export const formatDateForAPI = (date: string | Date | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    
    return dateObj.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

/**
 * Get merged application configuration
 * This function merges APP_CONFIG defaults with ApplicationSettings from the database.
 * 
 * @deprecated Use SettingsContext (useSettings hook) instead for runtime access to merged config
 * This function is kept for backward compatibility and static access
 */
export const getMergedAppConfig = () => {
  return APP_CONFIG;
};
